import { useNavigate } from 'react-router-dom';

export interface DefaultsCardDefaults {
  estimationType: string;
  deliveryMode: string;
  regulatoryContext: string[];
  industry: string | null;
  teamSize: string;
}

interface Props {
  defaults: DefaultsCardDefaults;
  onDismiss: () => void;
  onCreateProject: () => void;
  // D3 (#1247) — the setup-as-a-project RING completion signal, distinct from
  // the wizard signal that gates this card's visibility. When the ring flow is
  // still pending the card must NOT claim "ready": it shows accurate copy + a
  // "Continue setup →" CTA into the /setup hub. Defaults preserve the original
  // "ready" framing so callers that don't pass these (and existing tests) are
  // unaffected.
  setupComplete?: boolean;
  setupStepsRemaining?: number;
  // #1247 access-regression follow-on — per-caller /setup eligibility (same
  // predicate the /setup gate enforces). Gates ONLY the "Continue setup →"
  // button: an ineligible Buyer (a TL is now handling setup) still sees the
  // accurate amber "N steps remaining" status copy, but no button into a /setup
  // page the gate would 403 them out of (their surface is the dashboard panel).
  // Defaults to `false` (fail-closed): if eligibility is unknown, don't offer a
  // CTA that may dead-end in a redirect.
  setupAccessible?: boolean;
}

const SUCCESS_GREEN = '#22c55e';
// AC5 — incomplete-setup state is visually DISTINCT from the green "ready"
// state: amber accent + "in progress" framing rather than a success tick.
const IN_PROGRESS_AMBER = '#d97706';

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  marginBottom: 2,
};

const valueStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text)',
};

const linkButtonStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--primary)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
};

function formatEstimationType(est: string): string {
  switch (est) {
    case 'EU_RU': return 'EU/RU';
    case 'STORY_POINTS': return 'Story Points';
    case 'BOTH': return 'Both (EU/RU + Story Points)';
    default: return est;
  }
}

export default function DefaultsCard({
  defaults,
  onDismiss,
  onCreateProject,
  setupComplete = true,
  setupStepsRemaining = 0,
  setupAccessible = false,
}: Props) {
  const navigate = useNavigate();

  const accent = setupComplete ? SUCCESS_GREEN : IN_PROGRESS_AMBER;
  const stepWord = setupStepsRemaining === 1 ? 'step' : 'steps';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="defaults-card"
      style={{
        position: 'relative',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeft: `4px solid ${accent}`,
        borderRadius: 'var(--radius-sm)',
        padding: '20px 24px',
        marginBottom: 24,
      }}
    >
      {/* Dismiss button */}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss defaults card"
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          background: 'none',
          border: 'none',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          fontSize: 16,
          padding: 4,
          lineHeight: 1,
        }}
      >
        ✕
      </button>

      {/* Header — AC1/AC5: copy reflects the ring-flow completion signal.
          Setup complete → green "ready"; setup pending → amber "created, N
          steps remaining" (never claims "ready" while the ring pends). */}
      <div style={{ marginBottom: 16 }}>
        <h3
          data-testid="defaults-card-title"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span aria-hidden="true" style={{ color: accent }}>
            {setupComplete ? '✓' : '⚙'}
          </span>
          {setupComplete
            ? 'Your workspace is ready'
            : `Workspace created — ${setupStepsRemaining} setup ${stepWord} remaining`}
        </h3>
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 13,
            color: 'var(--text-secondary)',
            margin: '4px 0 0',
          }}
        >
          {setupComplete
            ? "Here's what we've set up for you based on your onboarding."
            : 'Finish setting up BluePrint to connect your tools and unlock everything.'}
        </p>
      </div>

      {/* Two-column defaults grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 32px', marginBottom: 16 }}>
        <div>
          <div style={labelStyle}>Estimation type</div>
          <div style={valueStyle}>{formatEstimationType(defaults.estimationType)}</div>
        </div>
        <div>
          <div style={labelStyle}>Compliance</div>
          <div style={valueStyle}>
            {defaults.regulatoryContext.length > 0
              ? defaults.regulatoryContext.join(', ')
              : 'None'}
          </div>
        </div>
        <div>
          <div style={labelStyle}>Delivery mode</div>
          <div style={valueStyle}>{defaults.deliveryMode}</div>
        </div>
        <div>
          <div style={labelStyle}>Team members</div>
          <div style={valueStyle}>{defaults.teamSize}</div>
        </div>
        {defaults.industry && (
          <div>
            <div style={labelStyle}>Industry</div>
            <div style={valueStyle}>{defaults.industry}</div>
          </div>
        )}
      </div>

      {/* Footer — AC1: a clear "Continue setup →" CTA into the /setup hub when
          the ring flow is still pending. Rendered as the prominent (filled)
          action so it reads as the primary next step over the secondary
          edit/create links.
          #1247 access-regression follow-on — ALSO gated on setupAccessible: the
          CTA only shows when the caller can actually enter /setup. An ineligible
          Buyer (a TL is handling setup) keeps the amber status copy above but
          gets no button into a page the gate would 403. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {!setupComplete && setupAccessible && (
          <button
            type="button"
            data-testid="defaults-card-continue-setup"
            onClick={() => navigate('/setup')}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              background: 'var(--primary)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            Continue setup →
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate('/settings/organisation')}
          style={linkButtonStyle}
        >
          Edit in Settings →
        </button>
        <button
          type="button"
          onClick={onCreateProject}
          style={linkButtonStyle}
        >
          Create your first project →
        </button>
      </div>
    </div>
  );
}
